import { useEffect, useMemo, useState } from 'react';
import Card from '@mui/material/Card';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import { useNavigate, useParams } from 'react-router-dom';

import SoftButton from 'components/SoftButton';
import SoftBox from 'components/SoftBox';
import SoftInput from 'components/SoftInput';
import { showSuiErrorSnackbar } from 'components/SuiSnackbar';
import SoftTypography from 'components/SoftTypography';

import { useAuth } from 'auth';
import {
  fetchEventByUuid,
  GooglePlacesAutocomplete,
  readCachedEventByUuid,
  updateEventRequest,
} from 'event';
import { CategorySelect } from 'profile';
import { type ProfileCategory, type ProfileSkill } from 'profile/profile.api';
import { SkillsAutocomplete } from 'profile/skills-autocomplete';
import { normalizeEndDateTimeForStartChange } from 'lib/datetime-local';
import { useLocale } from 'lib/i18n/client';
import { getTranslations, interpolateTemplate } from 'lib/i18n/dictionary';
import {
  createPositionRequest,
  fetchEventPositions,
  readCachedEventPositions,
  type PositionBudgetCurrency,
  type PositionSummary,
} from 'position';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import DataTable from 'examples/Tables/DataTable';
import Footer from 'examples/Footer';
import { CURRENCY_OPTIONS, DEFAULT_CURRENCY_CODE } from 'currency/currency';

function toDateTimeLocalValue(value: Date): string {
  const localValue = new Date(value.getTime() - value.getTimezoneOffset() * 60_000);
  return localValue.toISOString().slice(0, 16);
}

function getDefaultEventTimeRange() {
  const startsAt = new Date();
  const endsAt = new Date(startsAt.getTime() + 60 * 60 * 1000);

  return {
    startsAt: toDateTimeLocalValue(startsAt),
    endsAt: toDateTimeLocalValue(endsAt),
  };
}

function toSafeDateTimeLocalValue(value: string, fallbackValue: string): string {
  const parsedValue = new Date(value);
  if (!Number.isFinite(parsedValue.getTime())) {
    return fallbackValue;
  }

  return toDateTimeLocalValue(parsedValue);
}

function extractRegionFromCityLabel(cityLabel: string): string {
  const parts = cityLabel
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length < 2) {
    return '';
  }

  return parts.slice(1).join(', ');
}

interface PositionRow {
  uuid: string;
  title: string;
  status: string;
  budget: string;
  schedule: string;
  skills: string;
}

function formatCurrency(value: number, currency: PositionBudgetCurrency): string {
  if (!Number.isFinite(value)) {
    return '0.00';
  }

  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${currency} ${value.toFixed(2)}`;
  }
}

function formatBudget(
  position: PositionSummary,
  translations: ReturnType<typeof getTranslations>['positionsPage']
): string {
  const hasMin = typeof position.budgetMin === 'number';
  const hasMax = typeof position.budgetMax === 'number';

  if (!hasMin && !hasMax) {
    return translations.noBudget;
  }

  const currency = position.budgetCurrency ?? DEFAULT_CURRENCY_CODE;
  const minLabel = hasMin ? formatCurrency(position.budgetMin as number, currency) : null;
  const maxLabel = hasMax ? formatCurrency(position.budgetMax as number, currency) : null;

  if (minLabel && maxLabel) {
    return `${minLabel} - ${maxLabel}`;
  }

  if (minLabel) {
    return interpolateTemplate(translations.budgetFrom, { value: minLabel });
  }

  return maxLabel
    ? interpolateTemplate(translations.budgetUpTo, { value: maxLabel })
    : translations.noBudget;
}

function formatDateTime(value?: string | null): string {
  if (!value) {
    return '';
  }

  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) {
    return '';
  }

  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(timestamp));
  } catch {
    return new Date(timestamp).toISOString();
  }
}

function formatSchedule(
  position: PositionSummary,
  translations: ReturnType<typeof getTranslations>['positionsPage']
): string {
  const startLabel = formatDateTime(position.startsAt ?? undefined);
  const endLabel = formatDateTime(position.endsAt ?? undefined);

  if (!startLabel && !endLabel) {
    return translations.flexibleSchedule;
  }

  if (startLabel && endLabel) {
    return `${startLabel} → ${endLabel}`;
  }

  return startLabel || endLabel || translations.flexibleSchedule;
}

function formatSkills(
  skills: ProfileSkill[],
  translations: ReturnType<typeof getTranslations>['positionsPage']
): string {
  const labels = skills.map((skill) => skill.title).filter(Boolean);
  return labels.length ? labels.join(', ') : translations.noSkills;
}

function toPositionRows(
  positions: PositionSummary[],
  translations: ReturnType<typeof getTranslations>['positionsPage']
): PositionRow[] {
  return positions.map((position) => ({
    uuid: position.uuid,
    title: position.title,
    status: position.status,
    budget: formatBudget(position, translations),
    schedule: formatSchedule(position, translations),
    skills: formatSkills(position.skills ?? [], translations),
  }));
}

function EditEvent() {
  const navigate = useNavigate();
  const { uuid = '' } = useParams();
  const { account, accessToken } = useAuth();
  const locale = useLocale();
  const translations = getTranslations(locale);
  const eventTranslations = translations.eventsPage;
  const positionTranslations = translations.positionsPage;
  const cachedEvent = uuid.trim() ? readCachedEventByUuid(uuid) : undefined;
  const cachedPositions = uuid.trim() ? readCachedEventPositions(uuid) : undefined;

  const [isLoading, setIsLoading] = useState(() => cachedEvent === undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [city, setCity] = useState('');
  const [cityPlaceId, setCityPlaceId] = useState<string | null>(null);
  const [cityCoordinates, setCityCoordinates] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [location, setLocation] = useState('');
  const hasSelectedCity = city.trim().length > 0;
  const selectedRegion = extractRegionFromCityLabel(city);
  const [startsAtInput, setStartsAtInput] = useState(() => getDefaultEventTimeRange().startsAt);
  const [endsAtInput, setEndsAtInput] = useState(() => getDefaultEventTimeRange().endsAt);
  const [errorMessage, setErrorMessage] = useState('');

  const [positions, setPositions] = useState<PositionSummary[]>(() => cachedPositions ?? []);
  const [isPositionsLoading, setIsPositionsLoading] = useState(
    () => cachedPositions === undefined
  );
  const [positionsErrorMessage, setPositionsErrorMessage] = useState('');
  const [isCreatePositionDialogOpen, setIsCreatePositionDialogOpen] = useState(false);
  const [isPositionSubmitting, setIsPositionSubmitting] = useState(false);
  const [positionSubmitErrorMessage, setPositionSubmitErrorMessage] = useState('');
  const [positionTitle, setPositionTitle] = useState('');
  const [positionDescription, setPositionDescription] = useState('');
  const [positionLocation, setPositionLocation] = useState('');
  const [positionLocationPlaceId, setPositionLocationPlaceId] = useState<string | null>(null);
  const [positionLocationCoordinates, setPositionLocationCoordinates] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [positionBudgetMin, setPositionBudgetMin] = useState('');
  const [positionBudgetMax, setPositionBudgetMax] = useState('');
  const [positionBudgetCurrency, setPositionBudgetCurrency] =
    useState<PositionBudgetCurrency>(DEFAULT_CURRENCY_CODE);
  const [positionStartsAt, setPositionStartsAt] = useState('');
  const [positionEndsAt, setPositionEndsAt] = useState('');
  const [positionSkills, setPositionSkills] = useState<ProfileSkill[]>([]);
  const [positionCategories, setPositionCategories] = useState<ProfileCategory[]>([]);

  useEffect(() => {
    if (errorMessage) {
      showSuiErrorSnackbar(errorMessage);
    }
  }, [errorMessage]);

  useEffect(() => {
    if (positionsErrorMessage) {
      showSuiErrorSnackbar(positionsErrorMessage);
    }
  }, [positionsErrorMessage]);

  useEffect(() => {
    if (positionSubmitErrorMessage) {
      showSuiErrorSnackbar(positionSubmitErrorMessage);
    }
  }, [positionSubmitErrorMessage]);

  const applyEventDetails = (event: NonNullable<ReturnType<typeof readCachedEventByUuid>>) => {
    const defaultRange = getDefaultEventTimeRange();
    setTitle(event.title);
    setDescription(event.description ?? '');
    setCity(event.city?.label ?? '');
    setCityPlaceId(event.city?.placeId ?? null);
    setCityCoordinates(event.city?.coordinates ?? null);
    setLocation(event.location ?? '');
    setStartsAtInput(toSafeDateTimeLocalValue(event.startsAt, defaultRange.startsAt));
    setEndsAtInput(toSafeDateTimeLocalValue(event.endsAt, defaultRange.endsAt));
  };

  useEffect(() => {
    let isCancelled = false;

    async function loadEvent() {
      if (!uuid.trim()) {
        navigate('/events', { replace: true });
        return;
      }

      const cachedData = readCachedEventByUuid(uuid);
      if (!isCancelled && cachedData !== undefined) {
        if (cachedData) {
          applyEventDetails(cachedData);
        }
        setIsLoading(false);
      } else if (!isCancelled) {
        setIsLoading(true);
      }

      setErrorMessage('');

      try {
        const event = await fetchEventByUuid(uuid, accessToken ?? undefined);

        if (!event) {
          if (!isCancelled) {
            setErrorMessage(eventTranslations.notFound);
          }
          return;
        }

        if (!isCancelled) {
          applyEventDetails(event);
        }
      } catch (error) {
        if (!isCancelled) {
          setErrorMessage(
            error instanceof Error
              ? error.message
              : eventTranslations.updateFailed
          );
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    }

    void loadEvent();

    return () => {
      isCancelled = true;
    };
  }, [accessToken, eventTranslations.updateFailed, navigate, uuid]);

  useEffect(() => {
    let isCancelled = false;

    async function loadPositions() {
      if (!uuid.trim()) {
        return;
      }

      const cachedData = readCachedEventPositions(uuid);
      if (!isCancelled && cachedData !== undefined) {
        setPositions(cachedData);
        setIsPositionsLoading(false);
      } else if (!isCancelled) {
        setIsPositionsLoading(true);
      }

      setPositionsErrorMessage('');

      try {
        const eventPositions = await fetchEventPositions(uuid, accessToken ?? undefined);
        if (!isCancelled) {
          setPositions(eventPositions);
        }
      } catch (error) {
        if (!isCancelled) {
          setPositionsErrorMessage(
            error instanceof Error
              ? error.message
              : eventTranslations.positionsLoadFailed
          );
        }
      } finally {
        if (!isCancelled) {
          setIsPositionsLoading(false);
        }
      }
    }

    void loadPositions();

    return () => {
      isCancelled = true;
    };
  }, [accessToken, eventTranslations.positionsLoadFailed, uuid]);

  const handleCityChange = (nextCity: string) => {
    setCity(nextCity);
    if (!nextCity.trim().length) {
      setCityPlaceId(null);
      setCityCoordinates(null);
      setLocation('');
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const normalizedTitle = title.trim();
    const normalizedDescription = description.trim();
    const normalizedCity = city.trim();
    const normalizedLocation = location.trim();
    const shouldUseLocation = normalizedCity.length > 0;
    const startsAtDate = new Date(startsAtInput);
    const endsAtDate = new Date(endsAtInput);

    if (normalizedTitle.length < 2) {
      setErrorMessage(eventTranslations.validationTitleMin);
      return;
    }

    if (normalizedDescription.length > 2000) {
      setErrorMessage(eventTranslations.validationDescriptionMax);
      return;
    }

    if (normalizedCity.length > 512) {
      setErrorMessage(eventTranslations.validationCityMax);
      return;
    }

    if (shouldUseLocation && normalizedLocation.length > 255) {
      setErrorMessage(eventTranslations.validationLocationMax);
      return;
    }

    if (!startsAtInput || !Number.isFinite(startsAtDate.getTime())) {
      setErrorMessage(eventTranslations.validationStartInvalid);
      return;
    }

    if (!endsAtInput || !Number.isFinite(endsAtDate.getTime())) {
      setErrorMessage(eventTranslations.validationEndInvalid);
      return;
    }

    if (endsAtDate.getTime() <= startsAtDate.getTime()) {
      setErrorMessage(eventTranslations.validationEndAfterStart);
      return;
    }

    setErrorMessage('');
    setIsSubmitting(true);

    try {
      await updateEventRequest(
        {
          uuid,
          title: normalizedTitle,
          description: normalizedDescription || undefined,
          city: normalizedCity
            ? {
                label: normalizedCity,
                placeId: cityPlaceId,
                coordinates: cityCoordinates,
              }
            : null,
          location: shouldUseLocation ? normalizedLocation || null : null,
          startsAt: startsAtDate.toISOString(),
          endsAt: endsAtDate.toISOString(),
        },
        accessToken ?? undefined
      );
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : eventTranslations.updateFailed
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetPositionForm = () => {
    setPositionTitle('');
    setPositionDescription('');
    setPositionLocation('');
    setPositionLocationPlaceId(null);
    setPositionLocationCoordinates(null);
    setPositionBudgetMin('');
    setPositionBudgetMax('');
    setPositionBudgetCurrency(DEFAULT_CURRENCY_CODE);
    setPositionStartsAt('');
    setPositionEndsAt('');
    setPositionSkills([]);
    setPositionCategories([]);
  };

  const handleOpenCreatePositionDialog = () => {
    const defaultPositionLocation = (location.trim() || city.trim()).slice(0, 128);
    resetPositionForm();
    setPositionLocation(defaultPositionLocation);
    setPositionStartsAt(startsAtInput);
    setPositionEndsAt(endsAtInput);
    setPositionSubmitErrorMessage('');
    setIsCreatePositionDialogOpen(true);
  };

  const handleStartsAtInputChange = (nextValue: string) => {
    setStartsAtInput(nextValue);
    setEndsAtInput((currentEndsAtInput) =>
      normalizeEndDateTimeForStartChange(nextValue, currentEndsAtInput)
    );
  };

  const handlePositionStartsAtChange = (nextValue: string) => {
    setPositionStartsAt(nextValue);
    setPositionEndsAt((currentPositionEndsAt) =>
      normalizeEndDateTimeForStartChange(nextValue, currentPositionEndsAt)
    );
  };

  const handleCloseCreatePositionDialog = () => {
    if (isPositionSubmitting) {
      return;
    }

    setIsCreatePositionDialogOpen(false);
  };

  const handleCreatePosition = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const normalizedTitle = positionTitle.trim();
    const normalizedDescription = positionDescription.trim();
    const normalizedLocation = positionLocation.trim();

    if (!uuid.trim()) {
      setPositionSubmitErrorMessage(positionTranslations.validationEventMissing);
      return;
    }

    if (!account?.uuid) {
      setPositionSubmitErrorMessage(positionTranslations.validationAccountMissing);
      return;
    }

    if (normalizedTitle.length < 2) {
      setPositionSubmitErrorMessage(positionTranslations.validationTitleMin);
      return;
    }

    if (normalizedDescription.length > 2000) {
      setPositionSubmitErrorMessage(positionTranslations.validationDescriptionMax);
      return;
    }

    if (normalizedLocation.length > 128) {
      setPositionSubmitErrorMessage(positionTranslations.validationLocationMax);
      return;
    }

    const budgetMinValue = positionBudgetMin.trim().length
      ? Number(positionBudgetMin)
      : null;
    const budgetMaxValue = positionBudgetMax.trim().length
      ? Number(positionBudgetMax)
      : null;

    if (budgetMinValue !== null && (!Number.isFinite(budgetMinValue) || budgetMinValue < 0)) {
      setPositionSubmitErrorMessage(positionTranslations.validationBudgetMin);
      return;
    }

    if (budgetMaxValue !== null && (!Number.isFinite(budgetMaxValue) || budgetMaxValue < 0)) {
      setPositionSubmitErrorMessage(positionTranslations.validationBudgetMax);
      return;
    }

    if (budgetMinValue !== null && budgetMaxValue !== null && budgetMaxValue < budgetMinValue) {
      setPositionSubmitErrorMessage(positionTranslations.validationBudgetRange);
      return;
    }

    const hasStartInput = positionStartsAt.trim().length > 0;
    const hasEndInput = positionEndsAt.trim().length > 0;

    if (hasStartInput !== hasEndInput) {
      setPositionSubmitErrorMessage(positionTranslations.validationScheduleTogether);
      return;
    }

    let startsAtIso: string | null = null;
    let endsAtIso: string | null = null;

    if (hasStartInput && hasEndInput) {
      const startsAtDate = new Date(positionStartsAt);
      const endsAtDate = new Date(positionEndsAt);

      if (!Number.isFinite(startsAtDate.getTime())) {
        setPositionSubmitErrorMessage(positionTranslations.validationStartInvalid);
        return;
      }

      if (!Number.isFinite(endsAtDate.getTime())) {
        setPositionSubmitErrorMessage(positionTranslations.validationEndInvalid);
        return;
      }

      if (endsAtDate.getTime() <= startsAtDate.getTime()) {
        setPositionSubmitErrorMessage(positionTranslations.validationEndAfterStart);
        return;
      }

      startsAtIso = startsAtDate.toISOString();
      endsAtIso = endsAtDate.toISOString();
    }

    setPositionSubmitErrorMessage('');
    setIsPositionSubmitting(true);

    try {
      await createPositionRequest(
        {
          accountUuid: account.uuid,
          eventUuid: uuid,
          title: normalizedTitle,
          description: normalizedDescription || undefined,
          location: normalizedLocation
            ? {
                label: normalizedLocation,
                placeId: positionLocationPlaceId,
                coordinates: positionLocationCoordinates,
              }
            : null,
          budgetMin: budgetMinValue ?? undefined,
          budgetMax: budgetMaxValue ?? undefined,
          budgetCurrency: positionBudgetCurrency,
          startsAt: startsAtIso ?? undefined,
          endsAt: endsAtIso ?? undefined,
          skills: positionSkills.length ? positionSkills : undefined,
          categories: positionCategories.length ? positionCategories : undefined,
        },
        accessToken ?? undefined
      );

      resetPositionForm();
      setIsCreatePositionDialogOpen(false);

      const eventPositions = await fetchEventPositions(uuid, accessToken ?? undefined);
      setPositions(eventPositions);
    } catch (error) {
      setPositionSubmitErrorMessage(
        error instanceof Error ? error.message : positionTranslations.requestFailed
      );
    } finally {
      setIsPositionSubmitting(false);
    }
  };

  const positionTableData = useMemo(
    () => ({
      columns: [
        { Header: positionTranslations.tablePosition, accessor: 'title', width: '22%' },
        { Header: positionTranslations.tableStatus, accessor: 'status', width: '12%' },
        { Header: positionTranslations.tableBudget, accessor: 'budget', width: '16%' },
        { Header: positionTranslations.tableSchedule, accessor: 'schedule', width: '20%' },
        { Header: positionTranslations.tableSkills, accessor: 'skills' },
      ],
      rows: toPositionRows(positions, positionTranslations),
    }),
    [positionTranslations, positions]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <SoftBox p={3}>
                <SoftBox mb={2}>
                  <SoftTypography variant="h5" fontWeight="medium">
                    {eventTranslations.updateTitle}
                  </SoftTypography>
                  <SoftTypography variant="button" color="text" fontWeight="regular">
                    {eventTranslations.updateDescription}
                  </SoftTypography>
                </SoftBox>
                {isLoading ? (
                  <SoftTypography variant="button" color="text">
                    {eventTranslations.loadingSingle}
                  </SoftTypography>
                ) : (
                  <SoftBox component="form" onSubmit={handleSubmit}>
                    <Stack spacing={2}>
                      <SoftInput
                        placeholder={eventTranslations.titlePlaceholder}
                        value={title}
                        onChange={(inputEvent) => setTitle(inputEvent.target.value)}
                      />
                      <SoftInput
                        placeholder={eventTranslations.descriptionPlaceholder}
                        multiline
                        rows={3}
                        value={description}
                        onChange={(inputEvent) => setDescription(inputEvent.target.value)}
                      />
                      <GooglePlacesAutocomplete
                        placeholder={eventTranslations.cityPlaceholder}
                        helperText={eventTranslations.cityHelper}
                        value={city}
                        onChange={handleCityChange}
                        onPlaceSelect={(place) => {
                          setCityPlaceId(place?.placeId ?? null);
                          setCityCoordinates(place?.coordinates ?? null);
                        }}
                        disabled={isSubmitting || isLoading}
                        restrictToCities
                      />
                      {hasSelectedCity ? (
                        <GooglePlacesAutocomplete
                          placeholder={eventTranslations.locationPlaceholder}
                          helperText={eventTranslations.locationHelper}
                          value={location}
                          onChange={setLocation}
                          disabled={isSubmitting || isLoading}
                          restrictToEstablishments
                          regionContext={selectedRegion}
                        />
                      ) : (
                        <SoftTypography variant="caption" color="text" fontWeight="regular">
                          {eventTranslations.selectCityFirst}
                        </SoftTypography>
                      )}
                      <SoftBox
                        display="grid"
                        gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
                        gap={2}
                      >
                        <SoftInput
                          type="datetime-local"
                          value={startsAtInput}
                          onChange={(inputEvent) =>
                            handleStartsAtInputChange(inputEvent.target.value)
                          }
                        />
                        <SoftInput
                          type="datetime-local"
                          value={endsAtInput}
                          onChange={(inputEvent) => setEndsAtInput(inputEvent.target.value)}
                        />
                      </SoftBox>
                      <SoftBox display="flex" gap={1}>
                        <SoftButton
                          type="submit"
                          variant="gradient"
                          color="info"
                          disabled={
                            isSubmitting ||
                            !title.trim() ||
                            !startsAtInput ||
                            !endsAtInput ||
                            isLoading
                          }
                        >
                          {isSubmitting ? eventTranslations.saving : eventTranslations.saveChanges}
                        </SoftButton>
                        <SoftButton
                          type="button"
                          variant="outlined"
                          color="secondary"
                          onClick={() => navigate('/events')}
                          disabled={isSubmitting}
                        >
                          {eventTranslations.cancel}
                        </SoftButton>
                      </SoftBox>
                    </Stack>
                  </SoftBox>
                )}
              </SoftBox>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card>
              <SoftBox p={3}>
                <SoftBox
                  display="flex"
                  flexDirection={{ xs: 'column', md: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                  gap={2}
                  mb={2}
                >
                  <SoftBox lineHeight={1}>
                    <SoftTypography variant="h5" fontWeight="medium">
                      {eventTranslations.positionsTitle}
                    </SoftTypography>
                    <SoftTypography variant="button" fontWeight="regular" color="text">
                      {eventTranslations.positionsDescription}
                    </SoftTypography>
                  </SoftBox>
                  <SoftButton
                    variant="gradient"
                    color="info"
                    onClick={handleOpenCreatePositionDialog}
                    disabled={!uuid.trim()}
                  >
                    {eventTranslations.requestPosition}
                  </SoftButton>
                </SoftBox>
                {isPositionsLoading ? (
                  <SoftTypography variant="button" color="text">
                    {eventTranslations.positionsLoading}
                  </SoftTypography>
                ) : positionTableData.rows.length === 0 ? (
                  <SoftTypography variant="button" color="text">
                    {eventTranslations.positionsEmpty}
                  </SoftTypography>
                ) : (
                  <DataTable
                    table={positionTableData}
                    canSearch
                    onRowClick={(row: PositionRow) => navigate(`/positions/${row.uuid}`)}
                  />
                )}
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
      <Footer />

      <Dialog
        open={isCreatePositionDialogOpen}
        onClose={handleCloseCreatePositionDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{positionTranslations.requestDialogTitle}</DialogTitle>
        <SoftBox component="form" onSubmit={handleCreatePosition}>
          <DialogContent>
            <Stack spacing={2}>
              <SoftInput
                placeholder={positionTranslations.titlePlaceholder}
                value={positionTitle}
                onChange={(inputEvent) => setPositionTitle(inputEvent.target.value)}
              />
              <SoftInput
                placeholder={positionTranslations.descriptionPlaceholder}
                multiline
                rows={3}
                value={positionDescription}
                onChange={(inputEvent) => setPositionDescription(inputEvent.target.value)}
              />
              <GooglePlacesAutocomplete
                placeholder={positionTranslations.locationPlaceholder}
                value={positionLocation}
                onChange={setPositionLocation}
                onPlaceSelect={(place) => {
                  setPositionLocationPlaceId(place?.placeId ?? null);
                  setPositionLocationCoordinates(place?.coordinates ?? null);
                }}
                disabled={isPositionSubmitting}
                restrictToEstablishments
                regionContext={selectedRegion}
              />
              <FormControl fullWidth>
                <SoftBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    {positionTranslations.budgetCurrencyLabel}
                  </SoftTypography>
                </SoftBox>
                <Select
                  input={<SoftInput />}
                  value={positionBudgetCurrency}
                  onChange={(inputEvent) =>
                    setPositionBudgetCurrency(
                      inputEvent.target.value as PositionBudgetCurrency
                    )
                  }
                >
                  {CURRENCY_OPTIONS.map((currencyOption) => (
                    <MenuItem key={currencyOption.value} value={currencyOption.value}>
                      {currencyOption.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <SoftBox display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={2}>
                <SoftInput
                  type="number"
                  placeholder={positionTranslations.budgetMinPlaceholder}
                  value={positionBudgetMin}
                  onChange={(inputEvent) => setPositionBudgetMin(inputEvent.target.value)}
                  inputProps={{ min: 0, step: '0.01' }}
                />
                <SoftInput
                  type="number"
                  placeholder={positionTranslations.budgetMaxPlaceholder}
                  value={positionBudgetMax}
                  onChange={(inputEvent) => setPositionBudgetMax(inputEvent.target.value)}
                  inputProps={{ min: 0, step: '0.01' }}
                />
              </SoftBox>
              <SoftBox display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={2}>
                <SoftInput
                  type="datetime-local"
                  value={positionStartsAt}
                  onChange={(inputEvent) =>
                    handlePositionStartsAtChange(inputEvent.target.value)
                  }
                />
                <SoftInput
                  type="datetime-local"
                  value={positionEndsAt}
                  onChange={(inputEvent) => setPositionEndsAt(inputEvent.target.value)}
                />
              </SoftBox>
              <SoftBox
                display="grid"
                gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
                gap={2}
              >
                <CategorySelect
                  accessToken={accessToken ?? null}
                  value={positionCategories}
                  onChange={setPositionCategories}
                  disabled={isPositionSubmitting}
                />
                <SkillsAutocomplete
                  accessToken={accessToken ?? null}
                  value={positionSkills}
                  onChange={setPositionSkills}
                  disabled={isPositionSubmitting}
                />
              </SoftBox>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <SoftButton
              type="button"
              variant="outlined"
              color="secondary"
              onClick={handleCloseCreatePositionDialog}
              disabled={isPositionSubmitting}
            >
              {positionTranslations.cancel}
            </SoftButton>
            <SoftButton
              type="submit"
              variant="gradient"
              color="info"
              disabled={isPositionSubmitting || !positionTitle.trim()}
            >
              {isPositionSubmitting
                ? positionTranslations.requestSubmitting
                : positionTranslations.requestSubmit}
            </SoftButton>
          </DialogActions>
        </SoftBox>
      </Dialog>
    </DashboardLayout>
  );
}

export default EditEvent;
